/* [04]-STUDIOS: stationary split-flap-style announcement rotation. No marquee. */
(() => {
  if (!('customElements' in window) || customElements.get('signal-board-04')) return;

  class SignalBoard04 extends HTMLElement {
    connectedCallback() {
      if (this._boardReady) return;
      this._boardReady = true;
      this.items = Array.from(this.querySelectorAll('.signal-board-04__message'));
      this.current = Math.max(0, this.items.findIndex((node) => node.classList.contains('is-active')));
      this.delay = Math.max(3000, Number(this.dataset.rotation) || 5000);
      this._listeners = new AbortController();
      const signal = this._listeners.signal;

      if (this.dataset.pauseHover === 'true') {
        this.addEventListener('mouseenter', () => this.stop(), { signal });
        this.addEventListener('mouseleave', () => {
          if (!this.contains(document.activeElement)) this.start();
        }, { signal });
      }
      this.addEventListener('click', (event) => {
        const trigger = event.target.closest?.('[data-signal-credit-open]');
        if (!trigger || !this.contains(trigger)) return;
        event.preventDefault();
        this.openCreditForm();
      }, { signal });

      this.addEventListener('focusin', () => this.stop(), { signal });
      this.addEventListener('focusout', () => {
        if (!this.contains(document.activeElement)) this.start();
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stop();
        else this.start();
      }, { signal });

      // Shopify theme editor: expose the selected message while editing.
      document.addEventListener('shopify:block:select', (event) => {
        const block = event.target?.closest?.('.signal-board-04__message');
        const index = this.items.indexOf(block);
        if (index >= 0) {
          this.stop();
          this.show(index);
        }
      }, { signal });
      document.addEventListener('shopify:block:deselect', () => this.start(), { signal });

      this.show(this.current);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this._formInterval) clearInterval(this._formInterval);
      if (this._formTimeout) clearTimeout(this._formTimeout);
      this._listeners?.abort();
      this._boardReady = false;
    }

    // Open only the Shopify Forms popup matching the explicitly configured form ID.
    // Shopify Forms uses shadow DOM and does not expose a supported public open API.
    // This intentionally targets its existing teaser, without duplicating signup collection.
    findFormEmbed() {
      const formId = String(this.dataset.shopifyFormId || '1182762').trim();
      if (!/^\d{5,12}$/.test(formId)) return null;
      const exact = new RegExp('(?:^|\\D)' + formId + '(?:\\D|$)');
      const hosts = Array.from(document.querySelectorAll('shopify-forms-embed'));
      for (const host of hosts) {
        const containers = [
          host,
          host.parentElement,
          host.parentElement?.parentElement,
          host.closest('[data-forms-id]'),
          host.closest('[id^="app-embed-container-"]')
        ].filter(Boolean);
        const matched = containers.some((element) => {
          const values = [
            element.id,
            element.getAttribute('data-forms-id'),
            element.getAttribute('data-form-id'),
            element.getAttribute('form-id'),
            element.getAttribute('data-shopify-form-id')
          ].filter(Boolean);
          return values.some(value => exact.test(value));
        });
        if (matched) return host;
      }
      return null;
    }

    findTeaser(root, depth = 0) {
      if (!root || depth > 3) return null;
      const selector = 'button[class*="teaser" i], [role="button"][class*="teaser" i]';
      const direct = root.querySelector?.(selector);
      if (direct) return direct;
      const nested = root.querySelectorAll?.('form-embed, shopify-forms-embed, [id="app-embed"]') || [];
      for (const element of nested) {
        if (element.shadowRoot) {
          const teaser = this.findTeaser(element.shadowRoot, depth + 1);
          if (teaser) return teaser;
        }
      }
      return null;
    }

    tryOpenShopifyForm() {
      const embed = this.findFormEmbed();
      if (!embed) return false;
      embed.setAttribute('reveal', 'true');
      const teaser = this.findTeaser(embed.shadowRoot);
      if (!teaser) return false;
      teaser.click();
      return true;
    }

    openCreditForm() {
      this.stop();
      if (this.tryOpenShopifyForm()) return;
      // Forms can load late, including inside a shadow root.
      // Retry only the configured form, without opening an unrelated popup.
      if (this._formInterval) clearInterval(this._formInterval);
      if (this._formTimeout) clearTimeout(this._formTimeout);
      const check = () => {
        if (!this.tryOpenShopifyForm()) return;
        clearInterval(this._formInterval);
        clearTimeout(this._formTimeout);
        this._formInterval = null;
        this._formTimeout = null;
      };
      this._formInterval = setInterval(check, 200);
      this._formTimeout = setTimeout(() => {
        clearInterval(this._formInterval);
        this._formInterval = null;
        this._formTimeout = null;
        this.showFormUnavailable();
      }, 4000);
    }

    showFormUnavailable() {
      const trigger = this.querySelector('[data-signal-credit-open]');
      if (!trigger) return;
      const previous = trigger.textContent;
      trigger.textContent = 'SIGN-UP TEMPORARILY UNAVAILABLE';
      trigger.setAttribute('aria-live', 'polite');
      setTimeout(() => {
        if (!trigger.isConnected) return;
        trigger.textContent = previous;
        trigger.removeAttribute('aria-live');
      }, 2800);
    }

    show(index) {
      if (!this.items?.length) return;
      this.current = ((index % this.items.length) + this.items.length) % this.items.length;
      this.items.forEach((item, i) => {
        const active = i === this.current;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', String(!active));
        item.querySelectorAll('a, button[data-signal-credit-open]').forEach((link) => {
          link.tabIndex = active ? 0 : -1;
        });
      });
    }

    start() {
      this.stop();
      if (this.items?.length < 2 || document.hidden) return;
      this._interval = window.setInterval(() => {
        if (this.dataset.pauseHover === 'true' && this.matches(':hover')) return;
        if (this.contains(document.activeElement)) return;
        this.show(this.current + 1);
      }, this.delay);
    }

    stop() {
      if (this._interval != null) {
        window.clearInterval(this._interval);
        this._interval = null;
      }
    }
  }

  customElements.define('signal-board-04', SignalBoard04);
})();
